import { sourcesAnneesArchives } from "./sources-annees-archives";
/**
 * Références et archives justifiant les affirmations et chiffres clés
 * publiés dans les fiches et rubriques éditoriales.
 *
 * Clé = `<type>/<slug>` (ex. `personnalite/jacques-mariani`).
 * À défaut d'entrée précise, un jeu de références par type est renvoyé.
 */
export type SourceRef = {
  /** Titre ou nature du document. */
  title: string;
  /** Éditeur / institution (Le Monde, INSEE, INA…). */
  publisher: string;
  /** Lien vers la source ou le fonds d'archives. */
  url: string;
  /** Date ou période de publication, si pertinente. */
  date?: string;
};

const inaCorse: SourceRef = {
  title: "Fonds d'archives audiovisuelles sur la Corse",
  publisher: "INA — Institut national de l'audiovisuel",
  url: "https://www.ina.fr/recherche?q=corse",
};

const corseMatin: SourceRef = {
  title: "Archives de presse quotidienne régionale",
  publisher: "Corse-Matin",
  url: "https://www.corsematin.com/",
};

const france3Corse: SourceRef = {
  title: "Actualité et magazines d'enquête en Corse",
  publisher: "France 3 Corse ViaStella",
  url: "https://france3-regions.franceinfo.fr/corse/",
};

const insee: SourceRef = {
  title: "Dossier complet et recensements de la population — Corse",
  publisher: "INSEE",
  url: "https://www.insee.fr/fr/statistiques?debut=0&theme=1&geo=REG-94",
};

const legifrance: SourceRef = {
  title: "Textes en vigueur relatifs au statut de la Corse",
  publisher: "Légifrance",
  url: "https://www.legifrance.gouv.fr/",
};

const assembleeCorse: SourceRef = {
  title: "Délibérations, sessions et composition de l'Assemblée de Corse",
  publisher: "Collectivité de Corse",
  url: "https://www.isula.corsica/assemblea/",
};

const justicePresse: SourceRef = {
  title: "Comptes rendus d'audience et chroniques judiciaires (Corse)",
  publisher: "Le Monde",
  url: "https://www.lemonde.fr/corse/",
};

const parisienJustice: SourceRef = {
  title: "Suivi des procès du grand banditisme corse",
  publisher: "Le Parisien — Faits divers",
  url: "https://www.leparisien.fr/faits-divers/",
};

const unesco: SourceRef = {
  title: "Listes du patrimoine mondial et du patrimoine immatériel",
  publisher: "UNESCO",
  url: "https://whc.unesco.org/fr/list/",
};

const pnrc: SourceRef = {
  title: "Milieux naturels, sentiers et réserves de Corse",
  publisher: "Parc naturel régional de Corse",
  url: "https://www.pnr.corsica/",
};

const gallica: SourceRef = {
  title: "Presse ancienne et documents numérisés sur la Corse",
  publisher: "BnF — Gallica",
  url: "https://gallica.bnf.fr/services/engine/search/sru?operation=searchRetrieve&query=corse",
};

/** Références génériques par type de page. */
const byKind: Record<string, SourceRef[]> = {
  personnalite: [justicePresse, france3Corse, corseMatin, inaCorse],
  dossier: [justicePresse, parisienJustice, france3Corse, inaCorse],
  affaire: [justicePresse, parisienJustice, france3Corse, inaCorse],
  lieu: [insee, pnrc, unesco, corseMatin],
  annee: [inaCorse, gallica, france3Corse, corseMatin],
  theme: [inaCorse, france3Corse, corseMatin, insee],
  assemblee: [assembleeCorse, legifrance, france3Corse],
  collection: [inaCorse, france3Corse, corseMatin],
  presse: [gallica, inaCorse, corseMatin],
  chronologie: [inaCorse, legifrance, justicePresse, gallica],
  best: [inaCorse, france3Corse, corseMatin],
  guide: [inaCorse, france3Corse, corseMatin, gallica],
};

/**
 * Références exactes citées sous chaque réponse de la FAQ /guides.
 * Chaque URL a été vérifiée accessible (HTTP 200) au moment de la publication.
 */
export const faqRefs: SourceRef[] = [
  {
    title: "Corse : le FLNC annonce l'abandon de la lutte armée",
    publisher: "franceinfo",
    url: "https://www.franceinfo.fr/societe/corse-le-flnc-annonce-l-abandon-de-la-lutte-armee_1691813.html",
    date: "25 juin 2014",
  },
  {
    title: "La collectivité de Corse : statut et compétences",
    publisher: "Vie-publique.fr",
    url: "https://www.vie-publique.fr/fiches/19620-la-collectivite-de-corse-statut-et-competences",
  },
  {
    title: "L'Assemblée de Corse vote la coofficialité du corse et du français",
    publisher: "BFMTV",
    url: "https://www.bfmtv.com/politique/l-assemblee-de-corse-vote-la-coofficialite-du-corse-et-du-francais_AN-201305170073.html",
    date: "17 mai 2013",
  },
  {
    title: "15 mai 1768 : Gênes cède la Corse à la France (traité de Versailles)",
    publisher: "Herodote.net",
    url: "https://www.herodote.net/15_mai_1768-evenement-17680515.php",
    date: "1768-1769",
  },
  {
    title: "9 septembre - 4 octobre 1943 : la libération de la Corse",
    publisher: "Chemins de mémoire — ministère des Armées",
    url: "https://www.cheminsdememoire.gouv.fr/fr/9-septembre-4-octobre-1943-la-liberation-de-la-corse",
    date: "1943",
  },
  {
    title: "Yvan Colonna est mort, trois semaines après son agression en prison",
    publisher: "Le Parisien",
    url: "https://www.leparisien.fr/faits-divers/yvan-colonna-est-mort-trois-semaines-apres-son-agression-par-un-detenu-radicalise-en-prison-21-03-2022-32HB4DTGMBCNPKV2RROGBKU4SY.php",
    date: "21 mars 2022",
  },
  {
    title: "Corse : le militant indépendantiste Yvan Colonna inhumé à Cargèse",
    publisher: "franceinfo",
    url: "https://www.franceinfo.fr/france/corse/corse/corse-le-militant-independantiste-yvan-colonna-inhume-a-cargese_5044201.html",
    date: "25 mars 2022",
  },
  {
    title: "Yvan Colonna condamné à perpétuité pour l'assassinat du préfet Erignac",
    publisher: "BFMTV",
    url: "https://www.bfmtv.com/police-justice/yvan-colonna-condamne-a-perpetuite-pour-l-assassinat-d-erignac_AN-201106200027.html",
    date: "20 juin 2011",
  },
  {
    title: "Brise de mer : une bande vieille de trente ans",
    publisher: "Le Parisien",
    url: "https://www.leparisien.fr/faits-divers/une-bande-vieille-de-trente-ans-16-11-2009-712540.php",
    date: "16 novembre 2009",
  },
  {
    title: "Gang de la Brise de mer : aux origines de la haine",
    publisher: "Le Parisien",
    url: "https://www.leparisien.fr/faits-divers/gang-de-la-brise-de-mer-aux-origines-de-la-haine-06-05-2024-SRFFH7OJ7JB37PQJ47D5MFLMNU.php",
    date: "6 mai 2024",
  },
  {
    title: "Banditisme : la Brise de mer, de la Corse au Gabon",
    publisher: "Le Parisien",
    url: "https://www.leparisien.fr/faits-divers/banditisme-la-brise-de-mer-de-la-corse-au-gabon-18-09-2017-7268067.php",
    date: "18 septembre 2017",
  },
  {
    title: "Rencontres de chants polyphoniques de Calvi — festival officiel",
    publisher: "U Svegliu Calvese",
    url: "https://www.rencontrespolyphoniques.com/",
  },
  {
    title: "Mafiosa — fiche série (Canal+, 2006-2014)",
    publisher: "AlloCiné",
    url: "https://www.allocine.fr/series/ficheserie_gen_cserie=3061.html",
  },
  {
    title: "Mafiosa en streaming et replay",
    publisher: "CANAL+",
    url: "https://www.canalplus.com/series/mafiosa/h/4444067_50001",
  },
  {
    title: "« À son image » de Thierry de Peretti — Quinzaine 2024",
    publisher: "Quinzaine des Cinéastes",
    url: "https://www.quinzaine-cineastes.fr/fr/film/a-son-image",
    date: "2024",
  },
  {
    title: "Corse ViaStella — toutes les vidéos en replay",
    publisher: "france.tv",
    url: "https://www.france.tv/france-3/corse-viastella/toutes-les-videos/",
  },
  {
    title: "Corse ViaStella : programmes, direct et replay",
    publisher: "France 3 Corse ViaStella",
    url: "https://france3-regions.franceinfo.fr/corse/programmes",
  },
  {
    title:
      "Mission de réflexion sur les documentaires empruntant aux codes du magazine et du reportage",
    publisher: "CNC",
    url: "https://www.cnc.fr/professionnels/etudes-et-rapports/rapport/compte-rendu-de-la-mission-de-reflexion-autour-des-documentaires-empruntant-aux-codes-du-magazine-et-du-reportage-et-formes-innovantes_231800",
  },
  {
    title: "Le cantu in paghjella, liste de sauvegarde urgente de l'UNESCO",
    publisher: "UNESCO",
    url: "https://ich.unesco.org/fr/USL/le-cantu-in-paghjella-profane-et-liturgique-de-corse-tradition-orale-00315",
    date: "2009",
  },
  {
    title: "Archives audiovisuelles sur la Corse",
    publisher: "INA",
    url: "https://www.ina.fr/recherche?q=chant+corse",
  },
  {
    title: "Reportage ou Documentaire",
    publisher: "Médialab — Université de Limoges",
    url: "https://www.unilim.fr/medialab/tutoriels-et-fiches-pratiques-04/",
  },
  {
    title: "Reportage ou documentaire ?",
    publisher: "Klip-Klap",
    url: "https://klip-klap.com/reportage-ou-documentaire/",
    date: "22 janvier 2018",
  },
];



/** Références précises, page par page. */
const bySlug: Record<string, SourceRef[]> = {
  ...sourcesAnneesArchives,
  "personnalite/jacques-mariani": [
    {
      title: "Le parrain corse Jacques Mariani condamné à six ans de prison pour un projet de crime",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/le-parrain-corse-jacques-mariani-condamne-a-six-ans-de-prison-pour-un-projet-de-crime-31-01-2025-LKET3K6J2RHFLOSDMOSNDOLGY4.php",
      date: "31 janvier 2025",
    },
    {
      title: "Banditisme corse : Jacques Mariani relaxé pour un projet d'assassinat",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/banditisme-corse-jacques-mariani-relaxe-pour-un-projet-dassassinat-26-01-2026-LRO76XYJ45AP7DPM25Z5E5XE2E.php",
      date: "26 janvier 2026",
    },
    {
      title: "Francis Mariani, un des « parrains » corses, retrouvé mort après l'explosion d'un hangar",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/societe/article/2009/01/16/francis-mariani-un-des-parrains-corses-retrouve-mort-apres-l-explosion-d-un-hangar_1142811_3224.html",
      date: "16 janvier 2009",
    },
  ],
  "personnalite/jacques-santoni": [
    {
      title: "Procès Sollacaro : le cas du principal accusé Jacques Santoni disjoint pour raisons de santé",
      publisher: "Ouest-France",
      url: "https://www.ouest-france.fr/corse/proces-sollacaro-le-cas-du-principal-accuse-jacques-santoni-disjoint-pour-raisons-de-sante-245e72f0-b8d8-11f0-9097-68e77b575996",
      date: "3 novembre 2025",
    },
    {
      title: "Le « parrain » présumé de la bande du Petit Bar placé en détention pour homicide",
      publisher: "BFMTV",
      url: "https://www.bfmtv.com/police-justice/le-parrain-presume-de-la-bande-du-petit-bar-place-en-detention-pour-homicide_AN-201505130020.html",
      date: "13 mai 2015",
    },
    {
      title:
        "Procès de la bande du Petit Bar : des peines allant jusqu'à 13 ans de prison et près de 10 millions d'euros d'amende",
      publisher: "Franceinfo",
      url: "https://www.franceinfo.fr/faits-divers/justice-proces/proces-de-la-bande-du-petit-bar-des-peines-allant-jusqu-a-13-ans-de-prison-et-pres-de-10-millions-d-euros-d-amende-prononcees_7277997.html",
      date: "28 mai 2025",
    },
    {
      title: "Un des ex-avocats de Colonna tué par balle à Ajaccio",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/societe/article/2012/10/16/un-des-ex-avocats-de-colonna-tue-par-balle-a-ajaccio_1775975_3224.html",
      date: "16 octobre 2012",
    },
  ],
  "personnalite/mickael-ettori": [
    {
      title:
        "Comment les gendarmes ont arrêté Mickaël Ettori, lieutenant de la bande criminelle corse du Petit Bar, après 1 901 jours de cavale",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/societe/article/2025/12/13/comment-les-gendarmes-ont-arrete-mickael-ettori-lieutenant-de-la-bande-criminelle-corse-du-petit-bar-apres-1-901-jours-de-cavale_6657209_3224.html",
      date: "13 décembre 2025",
    },
    {
      title:
        "Bande corse du Petit Bar : Mickaël Ettori condamné à 12 ans de prison et 1,5 million d'euros d'amende",
      publisher: "Franceinfo",
      url: "https://www.franceinfo.fr/faits-divers/justice-proces/bande-corse-du-petit-bar-mickael-ettori-condamne-a-12-ans-de-prison-et-1-5-million-d-euros-d-amende-pour-avoir-ete-le-financier-du-clan-criminel_7984583.html",
      date: "28 mai 2025",
    },
    {
      title: "Procès Email Diamant : Mickaël Ettori condamné à douze ans de prison",
      publisher: "France 3 Corse ViaStella",
      url: "https://france3-regions.franceinfo.fr/corse/corse-du-sud/proces-email-diamant-mickael-ettori-condamne-a-douze-ans-de-prison-et-un-million-d-euros-d-amende-3345679.html",
      date: "5 mai 2026",
    },
  ],
  "personnalite/jean-luc-germani": [
    {
      title: "Putsch au cercle Wagram : 6 ans ferme pour le Corse Jean-Luc Germani",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/putsch-au-cercle-wagram-6-ans-ferme-pour-le-corse-jean-luc-germani-18-06-2015-4873155.php",
      date: "18 juin 2015",
    },
    {
      title: "Un parrain corse interpellé près de Paris après 3 ans de cavale",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/un-parrain-corse-interpelle-pres-de-paris-apres-3-ans-de-cavale-27-11-2014-4327635.php",
      date: "27 novembre 2014",
    },
    france3Corse,
  ],
  "personnalite/edmond-simeoni": [inaCorse, france3Corse, corseMatin],
  "dossier/yvan-colonna": [
    {
      title: "Assassinat du préfet Érignac : chronologie de l'affaire",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/affaire-yvan-colonna/",
    },
    inaCorse,
    france3Corse,
  ],
  "annee/1975": [
    {
      title: "Aléria, août 1975 — journaux télévisés et magazines d'époque",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=aleria+1975",
      date: "1975",
    },
    gallica,
    france3Corse,
  ],
  "annee/1992": [
    {
      title: "Catastrophe de Furiani : bilan officiel et suites judiciaires",
      publisher: "INA / France 3 Corse",
      url: "https://www.ina.fr/recherche?q=furiani",
      date: "1992-2020",
    },
    {
      title: "Loi interdisant les matchs professionnels le 5 mai",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/",
      date: "2020",
    },
    corseMatin,
  ],
  "annee/1998": [
    {
      title: "Assassinat du préfet Claude Érignac — archives télévisées",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=erignac",
      date: "1998",
    },
    justicePresse,
    france3Corse,
  ],
  "annee/2015": [
    {
      title: "Résultats des élections territoriales de Corse 2015",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
      date: "décembre 2015",
    },
    france3Corse,
    corseMatin,
  ],
  "annee/1977": [
    {
      title: "Corse 1977 : attentats, FLNC et débat sur le statut — archives télévisées",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=corse+1977+flnc",
      date: "1977",
    },
    gallica,
    france3Corse,
  ],
  "annee/1978": [
    {
      title: "Corse 1978 : procès, clandestinité et revendication statutaire — archives",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=corse+1978",
      date: "1978",
    },
    gallica,
    justicePresse,
  ],
  "annee/2012": [
    {
      title: "Furiani, vingt ans après : bilan officiel de la catastrophe du 5 mai 1992",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=furiani+20+ans",
      date: "2012",
    },
    {
      title: "Chronique judiciaire et affaires corses de 2012",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/corse/",
      date: "2012",
    },
    france3Corse,
  ],
  "annee/2018": [
    {
      title: "Création de la Collectivité de Corse au 1er janvier 2018",
      publisher: "Collectivité de Corse",
      url: "https://www.isula.corsica/",
      date: "2018",
    },
    {
      title: "Déplacement du président de la République en Corse, février 2018",
      publisher: "Élysée",
      url: "https://www.elysee.fr/emmanuel-macron/2018/02/07/discours-du-president-de-la-republique-a-ajaccio",
      date: "février 2018",
    },
    france3Corse,
  ],
  "annee/2022": [
    {
      title: "Mars 2022 en Corse : mobilisations et suites judiciaires",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/corse/",
      date: "2022",
    },
    {
      title: "Épisode orageux du 18 août 2022 en Corse — bilan et alertes",
      publisher: "Météo-France",
      url: "https://meteofrance.com/actualites-et-dossiers",
      date: "août 2022",
    },
    france3Corse,
  ],
  "personnalite/gilles-simeoni": [
    {
      title: "Composition et présidence du Conseil exécutif de Corse",
      publisher: "Collectivité de Corse",
      url: "https://www.isula.corsica/",
    },
    {
      title: "Résultats des élections territoriales de Corse (2015, 2017, 2021)",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
    },
    france3Corse,
  ],
  "personnalite/gilles-giovannangeli": [
    {
      title: "Gilles Giovannangeli succède à Gilles Simeoni à la tête du conseil exécutif corse",
      publisher: "franceinfo (avec AFP)",
      url: "https://www.franceinfo.fr/france/corse/corse/gilles-giovannangeli-a-ete-elu-nouveau-president-du-conseil-executif-corse_7982507.html",
      date: "4 mai 2026",
    },
    {
      title: "Gilles Giovannangeli élu à la tête de l'exécutif corse, entre continuité et nouveaux équilibres",
      publisher: "Corse Net Infos",
      url: "https://www.corsenetinfos.corsica/Gilles-Giovannangeli-elu-a-la-tete-de-l-executif-corse-entre-continuite-et-nouveaux-equilibres_a90497.html",
      date: "4 mai 2026",
    },
    {
      title: "Corse : Gilles Giovannangeli élu nouveau président du conseil exécutif",
      publisher: "Sud Ouest (avec AFP)",
      url: "https://www.sudouest.fr/corse/corse-gilles-giovannangeli-elu-nouveau-president-du-conseil-executif-28930059.php",
      date: "4 mai 2026",
    },
    {
      title: "Composition et présidence du Conseil exécutif de Corse",
      publisher: "Collectivité de Corse",
      url: "https://www.isula.corsica/",
    },
  ],
  "personnalite/jean-charles-orsucci": [
    {
      title: "R\u00e9sultats 1er tour Municipales 2026 \u00e0 Bonifacio : Jean-Charles Orsucci r\u00e9\u00e9lu",
      publisher: "France 3 Corse ViaStella / franceinfo",
      url: "https://france3-regions.franceinfo.fr/corse/corse-du-sud/bonifacio/resultats-1er-tour-municipales-2026-a-bonifacio-jean-charles-orsucci-reelu-3304401.html",
      date: "15 mars 2026",
    },
    {
      title: "R\u00e9sultats des \u00e9lections municipales 2026 \u2014 liste « Bonifacio 21 »",
      publisher: "Mairie de Bonifacio",
      url: "https://www.bonifacio-mairie.fr/resultats-des-elections-municipales-2026/",
      date: "17 mars 2026",
    },
    {
      title: "Changement de pr\u00e9sidence \u00e0 l'ANEL : Jean-Charles Orsucci \u00e9lu pr\u00e9sident",
      publisher: "Association nationale des \u00e9lus des littoraux",
      url: "https://www.anel.asso.fr/2025/09/23/changement-de-presidence-a-lanel/",
      date: "23 septembre 2025",
    },
    {
      title: "Gouvernance de l'ANEL \u2014 bureau et pr\u00e9sidence",
      publisher: "Association nationale des \u00e9lus des littoraux",
      url: "https://www.anel.asso.fr/anel/gouvernance/",
    },
    {
      title: "R\u00e9\u00e9lu maire de Bonifacio, Jean-Charles Orsucci install\u00e9 par le conseil municipal",
      publisher: "Corse Net Infos",
      url: "https://www.corsenetinfos.corsica/Reelu-maire-de-Bonifacio-Jean-Charles-Orsucci-a-confie-son-discours-a-ChatGPT_a89794.html",
      date: "21 mars 2026",
    },
    france3Corse,
  ],

  "personnalite/marie-antoinette-maupertuis": [
    {
      title: "La Présidence de l'Assemblée de Corse — biographie et rapport « Autunumia è Benistà »",
      publisher: "Assemblée de Corse (Collectivité de Corse)",
      url: "https://www.isula.corsica/assemblea/La-Presidence_r6.html",
    },
    {
      title: "Marie-Antoinette Maupertuis, membre du Comité européen des régions",
      publisher: "Comité européen des régions",
      url: "https://www.cor.europa.eu/fr/membres/marie-antoinette-maupertuis",
    },
    {
      title: "Compte rendu in extenso des sessions 2026 — mandature 2021-2028",
      publisher: "Assemblée de Corse",
      url: "https://www.isula.corsica/file/248655/",
      date: "2026",
    },
    france3Corse,
  ],

  "personnalite/charles-pieri": [
    {
      title: "Relaxe pour Jean-Guy Talamoni, dix ans de prison pour Charles Pieri",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/relaxe-pour-jean-guy-talamoni-dix-ans-de-prison-pour-charles-pieri-21-05-2005-2005967639.php",
      date: "21 mai 2005",
    },
    {
      title: "Charles Pieri condamné à huit ans de prison par la cour d'appel de Paris",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/societe/article/2006/02/16/charles-pieri-condamne-a-huit-ans-de-prison-par-la-cour-d-appel-de-paris_742210_3224.html",
      date: "16 février 2006",
    },
    corseMatin,
  ],
  "dossier/brise-de-mer": [
    {
      title: "Le gang de la Brise de mer : enquêtes et procès",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    parisienJustice,
    inaCorse,
  ],
  "dossier/petit-bar": [
    {
      title: "Procès de la bande du Petit Bar à Aix-en-Provence et Marseille",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    corseMatin,
    france3Corse,
  ],
  "dossier/surtourisme": [
    {
      title: "Fréquentation touristique et capacité d'accueil de la Corse",
      publisher: "INSEE — Statistiques régionales",
      url: "https://www.insee.fr/fr/statistiques?debut=0&theme=15&geo=REG-94",
    },
    {
      title: "Population légale de la Corse (recensement)",
      publisher: "INSEE",
      url: "https://www.insee.fr/fr/statistiques/zones/1405599?debut=0&geo=REG-94",
    },
    {
      title: "Observatoire du tourisme : chiffres de la saison et flux de visiteurs",
      publisher: "Agence du tourisme de la Corse",
      url: "https://www.atc.corsica/",
    },
    {
      title: "Réglementation de la fréquentation des sites naturels protégés",
      publisher: "Office de l'environnement de la Corse",
      url: "https://www.oec.corsica/",
    },
    france3Corse,
  ],
  "dossier/petru-guelfucci": [
    {
      title: "Petru Guelfucci, parcours et discographie (« Corsica », 1991)",
      publisher: "Bibliothèque nationale de France — data.bnf.fr",
      url: "https://data.bnf.fr/fr/search?term=Petru+Guelfucci",
    },
    {
      title: "Archives télévisées et captations de Petru Guelfucci",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=petru%20guelfucci",
    },
    france3Corse,
  ],
  "dossier/bande-du-valinco": [
    {
      title: "Règlements de comptes dans le Valinco : chronologie judiciaire",
      publisher: "Corse-Matin",
      url: "https://www.corsematin.com/",
    },
    justicePresse,
    france3Corse,
  ],
  "dossier/assassinat-erignac": [
    {
      title: "Assassinat du préfet Claude Érignac : dossier et chronologie",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/affaire-yvan-colonna/",
    },
    {
      title: "Archives télévisées du 6 février 1998 et des marches silencieuses",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=erignac",
      date: "1998",
    },
    france3Corse,
  ],
  "dossier/assassinats-non-elucides": [
    {
      title: "Homicides non élucidés en Corse : bilans et suivis d'enquête",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    corseMatin,
    france3Corse,
  ],
  "dossier/richard-casanova": [
    {
      title: "Assassinat de Richard Casanova (avril 2008) et suites judiciaires",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
      date: "2008",
    },
    parisienJustice,
    corseMatin,
  ],
  "dossier/parrains-du-milieu-corse": [
    {
      title: "Figures du milieu corse : condamnations et procédures",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    parisienJustice,
    france3Corse,
  ],
  "dossier/murtoli": [
    {
      title: "Domaine de Murtoli : contentieux d'urbanisme et décisions de justice",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/immobilier/",
    },
    {
      title: "Protection du littoral et loi Littoral en Corse",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/",
    },
    corseMatin,
  ],
  "dossier/clan-orsoni": [
    {
      title: "Procès du clan Orsoni : verdicts et peines",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
      date: "2025",
    },
    corseMatin,
    france3Corse,
  ],
  "dossier/repentis-et-chossat": [
    {
      title: "Statut des repentis et témoignages de Claude Chossat",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    {
      title: "Cadre légal du repenti en droit français",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/",
    },
    parisienJustice,
  ],
  "dossier/omerta-et-lutte-antimafia": [
    {
      title: "Mobilisations antimafia en Corse : « Maffia Nò, A Vita Iè »",
      publisher: "France 3 Corse ViaStella",
      url: "https://france3-regions.franceinfo.fr/corse/",
    },
    justicePresse,
    corseMatin,
  ],
  "dossier/avocats-et-magistrats-vises": [
    {
      title: "Assassinat d'Antoine Sollacaro : procès et verdicts",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
      date: "2025",
    },
    parisienJustice,
    france3Corse,
  ],
  "dossier/dume-guidi": [
    {
      title: "Dominique Guidi — biographie, naissance à Porto-Vecchio le 6 février 1996 et parcours en club",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Dominique_Guidi",
    },
    {
      title: "Fiche joueur Dumè Guidi : poste, parcours (Borgo, Furiani-Agliani, GFC Ajaccio, SC Bastia depuis 2020)",
      publisher: "SC Bastia",
      url: "https://sc-bastia.corsica/blogs/fiches-joueurs/dume-guidi",
    },
    {
      title: "« J'ai toujours tout donné pour ces couleurs sans jamais tricher » : le défenseur Dumè Guidi quitte le SC Bastia",
      publisher: "France 3 Corse / franceinfo",
      url: "https://france3-regions.franceinfo.fr/corse/haute-corse/j-ai-toujours-tout-donne-pour-ces-couleurs-sans-jamais-tricher-le-defenseur-dume-guidi-quitte-le-sc-bastia-3349375.html",
      date: "11 mai 2026",
    },
    {
      title: "Football Ligue 3 : Dumè Guidi quitte le SC Bastia",
      publisher: "Alta Frequenza",
      url: "https://www.alta-frequenza.corsica/index.php/actu/football_ligue_3_dume_guidi_quitte_le_sc_bastia_128507",
      date: "11 mai 2026",
    },
    {
      title: "Statistiques et matchs disputés (121 matchs, 7 buts au SC Bastia entre 2020 et 2026)",
      publisher: "Wikipedia (EN)",
      url: "https://en.wikipedia.org/wiki/Dominique_Guidi",
    },
  ],
  "dossier/christophe-vincent": [
    {
      title: "Christophe Vincent — né le 8 novembre 1992 à Bastia, milieu de terrain",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Christophe_Vincent",
    },
    {
      title: "Fiche joueur Christophe Vincent : formation au Sporting, débuts en 2011, prêt au CA Bastia en 2013",
      publisher: "SC Bastia",
      url: "https://sc-bastia.corsica/blogs/fiches-joueurs/christophe-vincent",
    },
    {
      title: "Christophe Vincent, capitaine du SC Bastia : « Essayer de faire un peu plus cette saison »",
      publisher: "Corse-Matin",
      url: "https://www.corsematin.com/articles/video-christophe-vincent-capitaine-du-sc-bastia-essayer-de-faire-un-peu-plus-cette-saison-126112",
      date: "20 juin 2022",
    },
    {
      title: "Christophe Vincent quitte lui aussi le SC Bastia : « Ça ne se termine pas comme je l'aurais voulu »",
      publisher: "ici (France Bleu / France 3)",
      url: "https://www.ici.fr/corse/christophe-vincent-quitte-lui-aussi-le-sc-bastia-ca-ne-se-termine-pas-comme-je-l-aurais-voulu-6649000",
      date: "12 mai 2026",
    },
    {
      title: "Après 11 ans au SC Bastia, Christophe Vincent se confie sur son départ et son nouveau défi à Furiani",
      publisher: "Corse-Matin",
      url: "https://www.corsematin.com/article/sports/7432688716616314/apres-11-ans-au-sc-bastia-christophe-vincent-se-confie-sur-son-depart-et-son-nouveau-defi-a-furiani",
      date: "11 août 2026",
    },
  ],
  "dossier/sporting-club-bastia": [

    {
      title: "Palmarès et historique du SC Bastia",
      publisher: "Fédération française de football",
      url: "https://www.fff.fr/",
    },
    {
      title: "Finale de la Coupe UEFA 1978 : PSV Eindhoven - SC Bastia",
      publisher: "UEFA",
      url: "https://www.uefa.com/uefaeuropaleague/match/65088/",
      date: "1978",
    },
    {
      title: "Le SC Bastia placé en liquidation judiciaire et rétrogradé en National 3",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/sports/football/ligue-1/le-sc-bastia-en-liquidation-judiciaire-11-09-2017-7250181.php",
      date: "11 septembre 2017",
    },
    {
      title: "Le SC Bastia relégué en Ligue 3 après sa défaite face au Mans",
      publisher: "Corse Net Infos",
      url: "https://www.corsenetinfos.corsica/",
      date: "9 mai 2026",
    },
    {
      title: "Archives sportives : finales, Coupe UEFA 1978, Furiani",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=sporting+bastia",
    },
    corseMatin,
  ],
  "dossier/furiani-1992": [
    {
      title: "Catastrophe de Furiani : bilan, enquête et procès",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=furiani",
      date: "1992-1995",
    },
    {
      title: "Loi n° 2021-1360 du 20 octobre 2021 relative au gel des matchs de football professionnel le 5 mai",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000044229371",
      date: "20 octobre 2021",
    },
    {
      title: "Furiani, 5 mai 1992 : 19 morts et 2 357 blessés, trente ans après",
      publisher: "France 3 Corse / franceinfo",
      url: "https://france3-regions.franceinfo.fr/corse/",
      date: "3 mai 2022",
    },
    corseMatin,
  ],
  "dossier/ac-ajaccio": [
    {
      title: "L'AC Ajaccio promu en Ligue 1 en terminant deuxième de Ligue 2, Auxerre barragiste",
      publisher: "L'Équipe",
      url: "https://www.lequipe.fr/Football/Actualites/L-ac-ajaccio-promu-en-ligue-1-auxerre-barragiste/1333065",
      date: "mai 2022",
    },
    {
      title: "Athletic Club ajaccien : fondation le 1er mars 1910, stade et palmarès",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Athletic_Club_ajaccien",
    },
    {
      title: "Difficultés financières et rétrogradation administrative de l'AC Ajaccio",
      publisher: "Foot National",
      url: "https://www.foot-national.com/",
      date: "12 juillet 2023",
    },
    corseMatin,
  ],
  "dossier/gazelec-ajaccio": [
    {
      title: "Histoire du GFCA : fusion en 1960 du Football Club ajaccien (1910) et du Gazélec Corse Club",
      publisher: "GFC Ajaccio",
      url: "https://www.gfca-foot.corsica/club/histoire",
    },
    {
      title: "Le Gazélec Ajaccio accède à la Ligue 1",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/sports/football/le-gazelec-ajaccio-accede-a-la-ligue-1-15-05-2015-4772119.php",
      date: "15 mai 2015",
    },
    {
      title: "Le GFCA rétrogradé administrativement en National 3 par la DNCG",
      publisher: "France 3 Corse / franceinfo",
      url: "https://france3-regions.franceinfo.fr/corse/",
      date: "8 juillet 2021",
    },
  ],
  "dossier/frederic-antonetti": [
    {
      title: "Fiche Frédéric Antonetti : né le 19 août 1961 à Venzolasca, clubs entraînés",
      publisher: "L'Équipe",
      url: "https://www.lequipe.fr/Football/FootballFicheEntraineur124296.html",
    },
    {
      title: "Finale de la Coupe de la Ligue 1995 : Paris SG - SC Bastia (2-0)",
      publisher: "Fédération française de football",
      url: "https://www.fff.fr/",
      date: "3 mai 1995",
    },
    {
      title: "Frédéric Antonetti quitte ses fonctions de directeur technique du SC Bastia",
      publisher: "France 3 Corse / franceinfo",
      url: "https://france3-regions.franceinfo.fr/corse/",
      date: "octobre 2025",
    },
  ],
  "dossier/pascal-olmeta": [
    {
      title: "Pascal Olmeta, né le 7 avril 1961 à Bastia : carrière et clubs",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Pascal_Olmeta",
    },
    {
      title: "Pascal Olmeta à l'Olympique lyonnais (1993-1996) et à l'OM (1990-1993)",
      publisher: "OL Passé Présent",
      url: "https://www.ol-passe-present.fr/",
    },
    {
      title: "Association « Un sourire, un espoir pour la vie »",
      publisher: "Association Pascal Olmeta",
      url: "https://www.assopascalolmeta.com/",
    },
  ],
  "dossier/entraineurs-du-football-corse": [
    {
      title: "Laurent Peyrelade nommé entraîneur du SC Bastia",
      publisher: "L'Équipe",
      url: "https://www.lequipe.fr/Football/",
      date: "6 juin 2026",
    },
    {
      title: "François Ciccolini : parcours d'entraîneur (Porto-Vecchio, SC Bastia, GFCA)",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Fran%C3%A7ois_Ciccolini",
    },
    {
      title: "Anthony Lippini nommé entraîneur de l'équipe première de l'AC Ajaccio",
      publisher: "ici (France Bleu / France 3)",
      url: "https://www.ici.fr/corse/",
      date: "septembre 2025",
    },
  ],
  "dossier/legendes-du-football-corse": [
    {
      title: "Claude Papi : né le 16 avril 1949 à Porto-Vecchio, carrière au SEC Bastia de 1968 à 1983",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Claude_Papi",
    },
    {
      title: "Yannick Cahuzac, capitaine du SC Bastia de 2005 à 2017",
      publisher: "RC Lens",
      url: "https://rclens.fr/",
    },
    {
      title: "Archives des grandes figures du football corse",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=sporting+bastia",
    },
  ],
  "dossier/echecs-en-corse": [
    {
      title: "Léo Battesti, fondateur en 1998 de la Ligue corse des échecs et président jusqu'en 2020",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/L%C3%A9o_Battesti",
    },
    {
      title: "Match Anand - Karpov à Bastia et Ajaccio (3,5-0,5)",
      publisher: "ChessBase",
      url: "https://en.chessbase.com/",
      date: "31 octobre - 2 novembre 2009",
    },
    {
      title: "27e édition du Corsican Circuit",
      publisher: "Corse Net Infos",
      url: "https://www.corsenetinfos.corsica/",
      date: "2026",
    },
  ],
  "dossier/marc-andria-maurizzi": [
    {
      title: "Marc'Andria Maurizzi, né le 16 mai 2007 à Bastia, plus jeune grand maître français",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Marc%27Andria_Maurizzi",
    },
    {
      title: "Champion du monde junior 2023 (FIDE World Junior Championship, Mexico)",
      publisher: "FIDE",
      url: "https://www.fide.com/",
      date: "octobre 2023",
    },
    {
      title: "Marc'Andria Maurizzi champion de France 2025 à Vichy",
      publisher: "Fédération française des échecs",
      url: "https://www.echecs.asso.fr/",
      date: "24 août 2025",
    },
  ],
  "dossier/mafia-corse": [
    {
      title: "Criminalité organisée en Corse : enquêtes et procès",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    parisienJustice,
    france3Corse,
  ],
  "dossier/nationalisme-et-flnc": [
    {
      title: "Du FLNC (1976) aux processus d'autonomie : archives télévisées",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=flnc",
    },
    {
      title: "Statuts de la Corse (1982, 1991, 2002) et processus de Beauvau",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/",
    },
    france3Corse,
  ],
  "dossier/armata-corsa": [
    {
      title: "Assassinat de François Santoni le 17 août 2001 à Monacia-d'Aullène",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/archives/article/2001/08/18/francois-santoni-assassine-en-corse_4198930_1819218.html",
    },
    {
      title: "Jean-Michel Rossi abattu à L'Île-Rousse le 7 août 2000",
      publisher: "Libération",
      url: "https://www.liberation.fr/france/2000/08/08/jean-michel-rossi-assassine-a-l-ile-rousse_333866/",
    },
    {
      title: "Armata Corsa, dissidence du FLNC-Canal historique (1999) : archives télévisées",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=armata+corsa",
    },
    france3Corse,
  ],
  "collection/cantu-di-lotta": [
    {
      title: "Chant polyphonique corse : archives et captations",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=chant+corse",
    },
    {
      title: "Cantu in paghjella, patrimoine culturel immatériel (2009)",
      publisher: "UNESCO",
      url: "https://ich.unesco.org/fr/RL/le-cantu-in-paghjella-profane-et-liturgique-de-corse-00315",
      date: "2009",
    },
    france3Corse,
  ],
  "theme/gastronomie-et-terroir": [
    {
      title: "Brocciu — appellation d'origine protégée (fiche produit)",
      publisher: "INAO",
      url: "https://www.inao.gouv.fr/produit/3283",
    },
    {
      title: "Charcuterie de Corse / Salumi corsi — AOP coppa, lonzo et jambon sec",
      publisher: "INAO",
      url: "https://www.inao.gouv.fr/Les-signes-officiels-de-la-qualite-et-de-l-origine-SIQO/Appellation-d-origine-protegee-controlee-AOP-AOC",
    },
    {
      title: "Vins de Corse : appellations et cépages",
      publisher: "Vins de Corse (CIV Corse)",
      url: "https://www.vinsdecorse.com/",
    },
    corseMatin,
    france3Corse,
  ],
  "theme/mafia-et-grand-banditisme": [
    {
      title: "Criminalité organisée en Corse : dossiers judiciaires",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    parisienJustice,
    inaCorse,
  ],
  "theme/histoire-et-identite": [
    {
      title: "Maison Bonaparte, Ajaccio — musée national de la maison natale de Napoléon Ier",
      publisher: "Musées nationaux napoléoniens",
      url: "https://www.musees-nationaux-napoleoniens.fr/maison-bonaparte",
    },
    {
      title: "Napoléon Bonaparte — biographie et chronologie",
      publisher: "Fondation Napoléon",
      url: "https://www.napoleon.org/histoire-des-2-empires/biographies/napoleon-bonaparte/",
    },
    {
      title: "Pasquale Paoli et la République corse (1755-1769)",
      publisher: "Encyclopædia Britannica",
      url: "https://www.britannica.com/biography/Pasquale-Paoli",
    },
    {
      title: "Bataille de Ponte Novu, 8-9 mai 1769 : rattachement de la Corse à la France",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Bataille_de_Ponte_Novu",
    },
    inaCorse,
    france3Corse,
  ],
  "theme/tourisme-et-societe": [

    {
      title: "Fréquentation touristique et logement en Corse",
      publisher: "INSEE",
      url: "https://www.insee.fr/fr/statistiques?debut=0&theme=1&geo=REG-94",
    },
    {
      title: "Observatoire du tourisme et données de fréquentation",
      publisher: "Agence du tourisme de la Corse",
      url: "https://www.visit-corsica.com/",
    },
    corseMatin,
  ],
  "theme/nationalisme-corse": [
    {
      title: "Cinquante ans de mouvement nationaliste : archives audiovisuelles",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=nationalisme+corse",
    },
    {
      title: "Aléria, août 1975 : archives télévisées",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=aleria+1975",
    },
    {
      title: "Corse : le FLNC annonce l'abandon de la lutte armée",
      publisher: "franceinfo",
      url: "https://www.franceinfo.fr/societe/corse-le-flnc-annonce-l-abandon-de-la-lutte-armee_1691813.html",
      date: "25 juin 2014",
    },
    {
      title: "La collectivité de Corse : statut et compétences",
      publisher: "Vie-publique.fr",
      url: "https://www.vie-publique.fr/fiches/19620-la-collectivite-de-corse-statut-et-competences",
    },
    {
      title: "L'Assemblée de Corse vote la coofficialité du corse et du français",
      publisher: "BFMTV",
      url: "https://www.bfmtv.com/politique/l-assemblee-de-corse-vote-la-coofficialite-du-corse-et-du-francais_AN-201305170073.html",
      date: "17 mai 2013",
    },
    {
      title: "Yvan Colonna condamné à perpétuité pour l'assassinat du préfet Erignac",
      publisher: "BFMTV",
      url: "https://www.bfmtv.com/police-justice/yvan-colonna-condamne-a-perpetuite-pour-l-assassinat-d-erignac_AN-201106200027.html",
      date: "20 juin 2011",
    },
    {
      title: "Yvan Colonna est mort, trois semaines après son agression en prison",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/yvan-colonna-est-mort-trois-semaines-apres-son-agression-par-un-detenu-radicalise-en-prison-21-03-2022-32HB4DTGMBCNPKV2RROGBKU4SY.php",
      date: "21 mars 2022",
    },
    {
      title: "Statuts successifs de la collectivité de Corse",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/",
    },
    france3Corse,
  ],

  "theme/rallye-et-sport-auto": [
    {
      title: "Tour de Corse — histoire, palmarès et éditions WRC (1956-2019)",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Tour_de_Corse",
    },
    {
      title: "Tour de Corse 2019 — 62e édition, manche 4 du championnat du monde WRC",
      publisher: "Motorsport.com",
      url: "https://fr.motorsport.com/wrc/results/2019/tour-de-corse/",
      date: "31 mars 2019",
    },
    {
      title: "Attilio Bettega : la première tragédie majeure du WRC, Tour de Corse 1985",
      publisher: "RallySport Magazine",
      url: "https://rallysportmag.com/attilio-bettega-35-years-since-the-wrcs-first-major-tragedy/",
      date: "2 mai 2020",
    },
    {
      title: "Mort d'Henri Toivonen et Sergio Cresto au Tour de Corse (dépêche d'archive)",
      publisher: "UPI Archives",
      url: "https://www.upi.com/Archives/1986/05/02/Driver-and-navigator-killed-in-race-crash/3840515390400/",
      date: "2 mai 1986",
    },
    {
      title: "Liste des engagés du 26e Tour de Corse Historique (452 équipages)",
      publisher: "Auto Hebdo",
      url: "https://www.autohebdo.fr/actualites/classic/la-liste-des-engages-provisoire-du-tour-de-corse-historique-devoilee.html",
      date: "19 août 2026",
    },
    {
      title: "Tour de Corse Historique — 26e édition, 3 au 10 octobre 2026",
      publisher: "Organisation du Tour de Corse Historique",
      url: "https://www.tourdecorse-historique.fr/",
    },
    inaCorse,
  ],
  "theme/trail-et-outdoor": [
    {
      title: "François D'Haene pulvérise le record du GR20 en 29h46",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/sports/running/ultra-trail-francois-dhaene-pulverise-le-record-du-gr20-en-corse-09-07-2026-GLXXD345AFHRPB33ELFP62VNYQ.php",
      date: "9 juillet 2026",
    },
    {
      title: "GR20 : D'Haene brise le mur des 30 heures, record précédent de Lambert Santelli (30h25, 2021)",
      publisher: "Sport.fr",
      url: "https://www.sport.fr/running/gr20-francois-dhaene-brise-le-mur-des-30-heures-et-reprend-son-bien-en-corse-1467112.shtm",
      date: "10 juillet 2026",
    },
    {
      title: "Records de traversée du GR20 (FKT)",
      publisher: "Mon GR20",
      url: "https://mongr20.com/en/gr20-records",
      date: "9 juillet 2026",
    },
    {
      title: "Restonica Trail by UTMB — formats de course (UTC 100M, 110 km) et éditions",
      publisher: "UTMB World Series",
      url: "https://restonica.utmb.world/",
    },
    pnrc,
  ],
  "guide/tour-de-corse": [
    {
      title: "Tour de Corse — histoire, palmarès et éditions WRC (1956-2019)",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Tour_de_Corse",
    },
    {
      title: "Tour de Corse 2019 — 62e édition, manche 4 du championnat du monde WRC",
      publisher: "Motorsport.com",
      url: "https://fr.motorsport.com/wrc/results/2019/tour-de-corse/",
      date: "31 mars 2019",
    },
    {
      title: "Mort d'Henri Toivonen et Sergio Cresto au Tour de Corse (dépêche d'archive)",
      publisher: "UPI Archives",
      url: "https://www.upi.com/Archives/1986/05/02/Driver-and-navigator-killed-in-race-crash/3840515390400/",
      date: "2 mai 1986",
    },
    {
      title: "Tour de Corse Historique — 26e édition, 3 au 10 octobre 2026",
      publisher: "Organisation du Tour de Corse Historique",
      url: "https://www.tourdecorse-historique.fr/",
    },
  ],
  "guide/gr20-trail": [
    {
      title: "François D'Haene pulvérise le record du GR20 en 29h46",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/sports/running/ultra-trail-francois-dhaene-pulverise-le-record-du-gr20-en-corse-09-07-2026-GLXXD345AFHRPB33ELFP62VNYQ.php",
      date: "9 juillet 2026",
    },
    {
      title: "GR20 : D'Haene brise le mur des 30 heures, record précédent de Lambert Santelli (30h25, 2021)",
      publisher: "Sport.fr",
      url: "https://www.sport.fr/running/gr20-francois-dhaene-brise-le-mur-des-30-heures-et-reprend-son-bien-en-corse-1467112.shtm",
      date: "10 juillet 2026",
    },
    {
      title: "Restonica Trail by UTMB — formats de course (UTC 100M, 110 km) et éditions",
      publisher: "UTMB World Series",
      url: "https://restonica.utmb.world/",
    },
  ],
  "theme/velo-et-gt20": [
    {
      title: "Grande Traversée de la Corse à vélo (GT20) — itinéraire, distance et dénivelé",
      publisher: "Visit Corsica / Agence du tourisme de la Corse",
      url: "https://www.visit-corsica.com/",
    },
    {
      title: "Tour de France 2013 : le Grand Départ en Corse, trois étapes inaugurales",
      publisher: "Le Tour de France (ASO)",
      url: "https://www.letour.fr/fr/histoire/2013",
    },
    {
      title: "BikingMan Corsica — épreuve d'ultra-cyclisme en autosuffisance, parcours et éditions",
      publisher: "BikingMan",
      url: "https://www.bikingman.com/",
    },
    {
      title: "Col de Vergio (1 477 m), plus haut col routier de Corse",
      publisher: "Wikipédia",
      url: "https://fr.wikipedia.org/wiki/Col_de_Vergio",
    },
  ],
  "theme/mer-et-plongee": [
    {
      title: "Golfe de Porto : calanche de Piana, golfe de Girolata, réserve de Scandola — inscription 1983",
      publisher: "UNESCO, Centre du patrimoine mondial",
      url: "https://whc.unesco.org/fr/list/258/",
    },
    {
      title: "Réserve naturelle de Scandola — création en 1975, zones de protection intégrale",
      publisher: "Parc naturel régional de Corse",
      url: "https://www.pnr.corsica/",
    },
    {
      title: "L'épave du B-17 de la baie de Calvi, immergée depuis 1944",
      publisher: "Ministère de la Culture, Atlas des patrimoines / DRASSM",
      url: "https://www.culture.gouv.fr/Thematiques/Archeologie/Archeologie-sous-les-eaux",
    },
    {
      title: "Réserve naturelle des Bouches de Bonifacio — réglementation de la pêche et de la plongée",
      publisher: "Office de l'environnement de la Corse",
      url: "https://www.oec.corsica/",
    },
  ],
  "theme/sport-et-football": [

    {
      title: "Palmarès des clubs corses et compétitions nationales",
      publisher: "Fédération française de football",
      url: "https://www.fff.fr/",
    },
    {
      title: "Archives sportives corses (finales, Furiani, derbys)",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=sporting+bastia",
    },
    corseMatin,
  ],
  "theme/religion-et-confreries": [
    {
      title: "Confréries, Granitula et Semaine sainte en Corse",
      publisher: "Ministère de la Culture — patrimoine immatériel",
      url: "https://www.culture.gouv.fr/",
    },
    inaCorse,
    france3Corse,
  ],
  "theme/franc-maconnerie-et-reseaux": [
    {
      title: "Effectifs et implantations maçonniques en Corse : enquêtes",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/societe/",
    },
    corseMatin,
    france3Corse,
  ],
  "lieu/ajaccio": [insee, corseMatin, unesco],
  "lieu/bastia": [insee, corseMatin, inaCorse],
  "lieu/bonifacio": [insee, pnrc, corseMatin],
  "lieu/cap-corse": [insee, inaCorse, corseMatin],
  "lieu/corte": [insee, inaCorse, corseMatin],
  "lieu/calvi-balagne": [insee, corseMatin, france3Corse],
  "lieu/scandola-piana": [unesco, pnrc, france3Corse],
  "lieu/niolu": [pnrc, inaCorse, corseMatin],
  "lieu/castagniccia": [insee, inaCorse, france3Corse],
  "lieu/sartenais-alta-rocca": [insee, inaCorse, corseMatin],
  "guide/histoire-de-la-corse": [
    {
      title: "Traité de Versailles (1768) et rattachement de la Corse à la France",
      publisher: "Bibliothèque nationale de France — Gallica",
      url: "https://gallica.bnf.fr/",
      date: "1768-1769",
    },
    {
      title: "La Corse, premier département français libéré (octobre 1943)",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=liberation+corse",
      date: "1943",
    },
    inaCorse,
  ],
  "guide/flnc-documentaire": [
    {
      title: "Le FLNC annonce l'abandon de la lutte armée",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/politique/",
      date: "juin 2014",
    },
    {
      title: "Archives audiovisuelles sur le FLNC et la clandestinité",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=flnc",
    },
    france3Corse,
  ],
  "guide/yvan-colonna": [
    {
      title: "Assassinat du préfet Claude Érignac, 6 février 1998",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=erignac",
      date: "1998",
    },
    {
      title: "Mort d'Yvan Colonna après son agression à la centrale d'Arles",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
      date: "mars 2022",
    },
    france3Corse,
  ],
  "guide/chant-corse": [
    {
      title: "Le Cantu in paghjella, patrimoine culturel immatériel",
      publisher: "UNESCO",
      url: "https://ich.unesco.org/fr/USL/le-cantu-in-paghjella-profane-et-liturgique-de-corse-tradition-orale-00315",
      date: "2009",
    },
    inaCorse,
  ],
  "guide/omerta-brise-de-mer": [
    {
      title: "Procès du « gang de la Brise de mer » et enquêtes sur le milieu corse",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    {
      title: "Règlements de comptes en Corse : archives audiovisuelles",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=brise+de+mer",
    },
    corseMatin,
  ],
  "guide/film-corse-netflix": [
    {
      title: "« À son image » de Thierry de Peretti, Quinzaine des Cinéastes",
      publisher: "Quinzaine des Cinéastes",
      url: "https://quinzaine-cineastes.fr/",
      date: "2024",
    },
    {
      title: "Fictions et séries tournées en Corse : archives et reportages",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=cinema+corse",
    },
    france3Corse,
  ],
  "guide/documentaire-corse-replay": [
    {
      title: "Documentaires et magazines corses en ligne",
      publisher: "France 3 Corse ViaStella",
      url: "https://france3-regions.franceinfo.fr/corse/",
    },
    inaCorse,
  ],
  "guide/mafia-corse-documentaire": [
    {
      title: "Enquêtes judiciaires sur le milieu corse (Brise de mer, Petit Bar)",
      publisher: "Le Monde",
      url: "https://www.lemonde.fr/police-justice/",
    },
    corseMatin,
    france3Corse,
  ],
  "guide/autonomie-corse": [
    {
      title: "La collectivité de Corse : statut et compétences",
      publisher: "Vie-publique.fr",
      url: "https://www.vie-publique.fr/fiches/19620-la-collectivite-de-corse-statut-et-competences",
    },
    legifrance,
    france3Corse,
  ],
  "guide/assemblee-de-corse": [
    assembleeCorse,
    {
      title: "Résultats des élections territoriales de Corse",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
    },
    france3Corse,
  ],
  "guide/elections-territoriales-corse": [
    {
      title: "Résultats des élections territoriales de Corse (2015, 2017, 2021)",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
    },
    {
      title: "La collectivité de Corse : statut et compétences",
      publisher: "Vie-publique.fr",
      url: "https://www.vie-publique.fr/fiches/19620-la-collectivite-de-corse-statut-et-competences",
    },
    france3Corse,
  ],
  "personnalite/jean-guy-talamoni": [
    {
      title: "Relaxe pour Jean-Guy Talamoni, dix ans de prison pour Charles Pieri",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/relaxe-pour-jean-guy-talamoni-dix-ans-de-prison-pour-charles-pieri-21-05-2005-2005967639.php",
      date: "21 mai 2005",
    },
    assembleeCorse,
    france3Corse,
  ],
  "personnalite/laurent-marcangeli": [
    {
      title: "Résultats des élections législatives et territoriales en Corse-du-Sud",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
    },
    corseMatin,
    france3Corse,
  ],
  "personnalite/jean-christophe-angelini": [
    {
      title: "Résultats des élections municipales et territoriales en Corse",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
    },
    assembleeCorse,
    france3Corse,
  ],
  "personnalite/jean-martin-mondoloni": [assembleeCorse, corseMatin, france3Corse],
  "personnalite/paul-felix-benedetti": [assembleeCorse, corseMatin, france3Corse],
  "personnalite/nicolas-battini": [
    {
      title: "Attentat à Corte : cinq à huit ans de prison pour trois nationalistes corses",
      publisher: "Le Parisien",
      url: "https://www.leparisien.fr/faits-divers/attentat-a-corte-5-a-8-ans-de-prison-pour-trois-nationalistes-corses-06-10-2016-6180535.php",
    },
    {
      title: "Battini, Tomasini, Verdi : un des derniers procès du nationalisme armé",
      publisher: "France 3 Corse ViaStella",
      url: "https://france3-regions.franceinfo.fr/corse/haute-corse/corte/battini-tomasini-verdi-derniers-proces-du-nationalisme-arme-1104397.html",
    },
    {
      title: "Qui est Nicolas Battini, candidat RN à Bastia, condamné pour « terrorisme » mais soutenu par Marine Le Pen ?",
      publisher: "RMC",
      url: "https://rmc.bfmtv.com/actualites/politique/qui-est-nicolas-battini-candidat-rn-a-bastia-condamne-pour-terrorisme-mais-soutenu-par-marine-le-pen_AV-202512030604.html",
    },
  ],
  "personnalite/paul-andre-colombani": [
    {
      title: "Résultats des élections législatives en Corse-du-Sud",
      publisher: "Ministère de l'Intérieur",
      url: "https://www.resultats-elections.interieur.gouv.fr/",
    },
    corseMatin,
    france3Corse,
  ],
  "personnalite/pascal-paoli": [
    {
      title: "Pasquale Paoli — notice biographique et Constitution corse de 1755",
      publisher: "Encyclopædia Universalis",
      url: "https://www.universalis.fr/encyclopedie/pascal-paoli/",
    },
    {
      title: "Université de Corse Pasquale Paoli — histoire de l'établissement fondé en 1765",
      publisher: "Università di Corsica Pasquale Paoli",
      url: "https://www.universita.corsica/fr/luniversite/histoire/",
    },
    inaCorse,
  ],
  "personnalite/napoleon-bonaparte": [
    {
      title: "Maison Bonaparte à Ajaccio — maison natale de Napoléon Ier",
      publisher: "Musées nationaux du palais de Fontainebleau / Maison Bonaparte",
      url: "https://www.musees-nationaux-malmaison.fr/maisonbonaparte/",
    },
    {
      title: "Napoléon Bonaparte — biographie et chronologie de référence",
      publisher: "Fondation Napoléon",
      url: "https://www.napoleon.org/histoire-des-2-empires/biographies/napoleon-bonaparte/",
    },
    inaCorse,
  ],
  "personnalite/danielle-casanova": [
    {
      title: "Danielle Casanova, née Vincentella Perini — notice biographique",
      publisher: "Le Maitron, dictionnaire biographique du mouvement ouvrier",
      url: "https://maitron.fr/spip.php?article19700",
    },
    {
      title: "Le convoi du 24 janvier 1943, dit « convoi des 31 000 »",
      publisher: "Mémorial de la Shoah",
      url: "https://ressources.memorialdelashoah.org/",
    },
    inaCorse,
  ],
  "personnalite/sampiero-corso": [
    {
      title: "Sampiero Corso (1498-1567) — notice biographique",
      publisher: "Encyclopædia Universalis",
      url: "https://www.universalis.fr/encyclopedie/sampiero-corso/",
    },
    {
      title: "La Corse génoise, XVIe siècle — ressources documentaires",
      publisher: "Bibliothèque nationale de France (Gallica)",
      url: "https://gallica.bnf.fr/services/engine/search/sru?operation=searchRetrieve&query=%28gallica%20all%20%22sampiero%20corso%22%29",
    },
    inaCorse,
  ],
  "personnalite/maria-gentile": [
    {
      title: "Maria Gentile, l'Antigone corse ? — conférence de l'Université de Corse",
      publisher: "France Culture / Università di Corsica Pasquale Paoli",
      url: "https://www.radiofrance.fr/franceculture/maria-gentile-l-antigone-corse-4398502",
    },
    {
      title: "Corse Antigone — L'Expérience (documentaire radiophonique de Marc Guidoni)",
      publisher: "France Culture",
      url: "https://www.radiofrance.fr/franceculture/podcasts/l-experience/corse-antigone-8601900",
    },
    {
      title: "Spaziu Maria Gentile — le complot d'Oletta de 1769 et son exécution",
      publisher: "Mairie d'Oletta",
      url: "https://www.oletta.fr/co/element/spaziu-maria-gentile-3",
    },
    {
      title: "Cunferenza — Maria Gentile, l'Antigone corse ?",
      publisher: "Médiathèque Culturelle de la Corse et des Corses (M3C)",
      url: "https://m3c.universita.corsica/s/fr/item/100716",
    },
    inaCorse,
    france3Corse,
  ],



  "personnalite/petru-giovacchini": [
    {
      title: "A Muvra et le corsisme de l'entre-deux-guerres — collection numérisée",
      publisher: "Bibliothèque nationale de France (Gallica)",
      url: "https://gallica.bnf.fr/services/engine/search/sru?operation=searchRetrieve&query=%28gallica%20all%20%22a%20muvra%22%29",
    },
    {
      title: "L'irrédentisme corse et la Libération — ressources documentaires",
      publisher: "INA — Institut national de l'audiovisuel",
      url: "https://www.ina.fr/recherche?q=irredentisme+corse",
    },
    corseMatin,
  ],
  "dossier/a-filetta": [
    {
      title: "A Filetta : notice d'autorité, discographie et enregistrements",
      publisher: "Bibliothèque nationale de France — data.bnf.fr",
      url: "https://data.bnf.fr/fr/search?term=A+Filetta",
    },
    {
      title: "Archives télévisées et captations d'A Filetta",
      publisher: "INA",
      url: "https://www.ina.fr/recherche?q=a%20filetta",
    },
    france3Corse,
  ],
};


/** Indique si la page dispose d'une référence exacte (et non d'un fonds générique). */
export function hasPreciseSources(key: string): boolean {
  return Boolean(bySlug[key]?.length);
}

/** Liste des clés disposant d'une référence exacte. */
export function preciseSourceKeys(): string[] {
  return Object.keys(bySlug);
}

/**
 * Renvoie les références d'une page : entrée précise si elle existe,
 * sinon le socle documentaire du type de page.
 */
export function sourcesFor(key: string | undefined): SourceRef[] {
  if (!key) return [];
  const exact = bySlug[key];
  if (exact) return exact;
  const kind = key.split("/")[0] ?? "";
  return byKind[kind] ?? [];
}

/**
 * Références propres à la fiche uniquement (aucun repli générique) — utilisé
 * pour le `sameAs` Schema.org, qui exige des pages traitant de l'entité.
 */
export function exactSourcesFor(key: string | undefined): SourceRef[] {
  if (!key) return [];
  return bySlug[key] ?? [];
}

/** Index de toutes les références connues, par URL. */
const byUrl: Record<string, SourceRef> = (() => {
  const out: Record<string, SourceRef> = {};
  for (const list of [...Object.values(bySlug), ...Object.values(byKind), faqRefs]) {
    for (const s of list) out[s.url] = s;
  }
  return out;
})();

/** Résout des URLs de références en fiches de source exploitables. */
export function sourcesByUrl(urls: string[] | undefined): SourceRef[] {
  if (!urls?.length) return [];
  return urls
    .map((u) => byUrl[u] ?? null)
    .filter((s): s is SourceRef => Boolean(s));
}
